import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { requestJson, getMediaUrl } from '../api';

const FLOW = ['PLACED', 'PACKED', 'SHIPPED', 'DELIVERED'];

const formatDateTime = (value) => {
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (value) => `\u20B9${Number(value || 0).toLocaleString('en-IN')}`;

const normalizeAddressLines = (address) => {
  if (!address) return [];
  if (typeof address === 'string') return [address];
  if (typeof address !== 'object') return [];

  const part = (v) => String(v || '').trim();
  const lines = [];

  const name = part(address.full_name || address.name || address.receiver_name);
  const phone = part(address.phone || address.mobile || address.contact);
  const line1 = part(address.address_line1 || address.line1 || address.street || address.address);
  const line2 = part(address.address_line2 || address.line2 || address.landmark);
  const city = part(address.city || address.town);
  const state = part(address.state || address.province);
  const pincode = part(address.pincode || address.zip || address.postal_code);
  const country = part(address.country);

  if (name) lines.push(name);
  if (phone) lines.push(phone);
  if (line1) lines.push(line1);
  if (line2) lines.push(line2);
  const cityStatePin = [city, state, pincode].filter(Boolean).join(', ');
  if (cityStatePin) lines.push(cityStatePin);
  if (country) lines.push(country);

  return lines.filter(Boolean);
};

const getAddressPhone = (address, fallback = '') => {
  if (!address || typeof address !== 'object') return String(fallback || '').trim();
  const value = (
    address.phone
    || address.mobile
    || address.contact
    || address.mobile_number
    || address.phone_number
    || fallback
  );
  return String(value || '').trim();
};

const getCustomerName = (address, fallback = '-') => {
  if (!address || typeof address !== 'object') return String(fallback || '-').trim();
  const value = (
    address.full_name
    || address.name
    || address.receiver_name
    || fallback
  );
  return String(value || '-').trim();
};

const OrdersDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await requestJson('GET', `/api/orders/admin/${id}/detail/`);
        setOrder(data);
      } catch (e) {
        setError(e.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  if (error) {
    return <div className="card">{error}</div>;
  }

  if (!order) {
    return <div className="card">Order not found</div>;
  }

  const currentStatus = String(order.status || '').toUpperCase();
  const currentIndex = FLOW.indexOf(currentStatus);
  const addressLines = normalizeAddressLines(order.address);
  const deliveryMobile = getAddressPhone(order.address, order.user_phone);
  const customerName = getCustomerName(order.address, order.user_id || '-');
  const customerMobile = String(order.user_phone || deliveryMobile || '').trim();

  const statusHistory = (Array.isArray(order.status_history) ? order.status_history : [])
    .map((entry, idx) => ({
      id: `${entry?.status || 'status'}-${idx}`,
      status: String(entry?.status || '').toUpperCase() || '-',
      at: formatDateTime(entry?.at),
      updatedBy: String(entry?.updated_by || '').trim(),
      reason: String(entry?.reason || '').trim(),
    }))
    .filter((entry) => entry.status !== '-');
  const cancellationReason = String(
    order.cancellation_reason
    || statusHistory.find((entry) => entry.status === 'CANCELLED')?.reason
    || ''
  ).trim();

  const cards = (order.items || []).map((item, idx) => {
    const variants = Array.isArray(item.variant_items) ? item.variant_items : [];
    const productsDetail = Array.isArray(item.products_detail) ? item.products_detail : [];
    const sizes = variants
      .map((variant, vIdx) => {
        const split = variant?.size_quantities && typeof variant.size_quantities === 'object'
          ? Object.entries(variant.size_quantities)
              .map(([size, qty]) => `${size}:${qty}`)
              .join(', ')
          : '';
        const packQty = Number(variant?.pack_quantity || 0);
        const packCount = Number(variant?.pack_count || variant?.quantity || 0);
        const pieces = packQty > 0 && packCount > 0 ? packQty * packCount : (packCount || packQty || 0);
        return {
          key: `variant-${idx}-${vIdx}`,
          color: variant?.color || variant?.variant_key || 'Default',
          split,
          packs: packQty || 0,
          perPack: packCount || 0,
          pieces,
        };
      })
      .filter(Boolean);

    return {
      key: `item-${idx}`,
      name: item.product_name || item.combo_name || 'Item',
      type: item.type || 'ITEM',
      image: item.image || (item.images && item.images[0]),
      subtotal: item.subtotal,
      unitPrice: item.unit_price || item.combo_price || 0,
      qty: item.quantity || item.pack_quantity || 1,
      variants: sizes,
      productsDetail,
    };
  });

  const totalLineItems = cards.reduce((sum, card) => {
    if (card.variants.length) {
      return sum + card.variants.reduce((variantSum, variant) => variantSum + Number(variant.pieces || 0), 0);
    }
    return sum + Number(card.qty || 0);
  }, 0);

  return (
    <div className="card order-detail-page">
      <div className="order-detail-head">
        <div>
          <h3>Order Detail</h3>
          <div className="muted order-detail-sub">Review shipping info and item-level breakdown.</div>
        </div>
        <button className="secondary" onClick={() => navigate('/admin/orders')}>Back to Orders</button>
      </div>

      <div className="order-detail-summary-grid">
        <div className="order-summary-card">
          <div className="order-summary-label">Order ID</div>
          <div className="order-summary-value order-summary-id">{order._id}</div>
        </div>
        <div className="order-summary-card">
          <div className="order-summary-label">Status</div>
          <div className={`order-summary-value order-summary-status badge-${currentStatus.toLowerCase()}`}>
            {currentStatus}
          </div>
        </div>
        <div className="order-summary-card">
          <div className="order-summary-label">Total Amount</div>
          <div className="order-summary-value">{formatCurrency(order.total_amount)}</div>
        </div>
        <div className="order-summary-card">
          <div className="order-summary-label">Placed At</div>
          <div className="order-summary-value">{formatDateTime(order.created_at)}</div>
        </div>
      </div>

      <div className="order-detail-grid">
        <div className="order-panel">
          <h4>Customer & Delivery</h4>
          <div className="order-panel-grid">
            <div className="order-panel-metric">
              <span>Customer</span>
              <strong>{customerName || '-'}</strong>
            </div>
            <div className="order-panel-metric">
              <span>Mobile</span>
              <strong>{customerMobile || '-'}</strong>
            </div>
            <div className="order-panel-metric">
              <span>Delivery Charge</span>
              <strong>{formatCurrency(order.delivery_charge)}</strong>
            </div>
            <div className="order-panel-metric">
              <span>Total Pieces</span>
              <strong>{totalLineItems}</strong>
            </div>
          </div>
          <div className="order-address-block">
            <div className="order-summary-label">Shipping Address</div>
            <div className="order-mobile-field">
              <label>Customer Mobile</label>
              <input value={customerMobile || ''} readOnly placeholder="No number added" />
            </div>
            {addressLines.length ? (
              <div className="order-address-lines">
                {addressLines.map((line, idx) => (
                  <div key={`addr-${idx}`}>{line}</div>
                ))}
              </div>
            ) : (
              <div className="muted">Address not available</div>
            )}
          </div>
        </div>

        <div className="order-panel">
          <h4>Shipping Timeline</h4>
          {currentStatus === 'CANCELLED' ? (
            <div className="order-ship-cancelled">
              Order cancelled. Shipping flow stopped.
              {cancellationReason ? (
                <div className="order-cancel-reason-text">Reason: {cancellationReason}</div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="order-detail-progress">
                <div
                  className="order-detail-progress-fill"
                  style={{
                    width: `${Math.max(0, (currentIndex / (FLOW.length - 1)) * 100)}%`,
                  }}
                />
              </div>
              <div className="order-detail-step-row">
                {FLOW.map((step) => {
                  const idx = FLOW.indexOf(step);
                  return (
                    <div
                      key={`detail-step-${step}`}
                      className={`order-detail-step ${idx < currentIndex ? 'done' : idx === currentIndex ? 'active' : ''}`}
                    >
                      <span className="order-detail-step-dot" />
                      <span>{step}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="order-history-list">
            {(statusHistory.length ? statusHistory : [{
              id: 'fallback',
              status: currentStatus || '-',
              at: formatDateTime(order.created_at),
              updatedBy: '',
            }]).map((entry) => (
              <div key={entry.id} className="order-history-item">
                <span className={`badge-${entry.status.toLowerCase()}`}>{entry.status}</span>
                <span className="muted">{entry.at}</span>
                {entry.updatedBy ? <span className="muted">by {entry.updatedBy}</span> : null}
                {entry.reason ? <span className="muted">Reason: {entry.reason}</span> : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="order-items-head row">
        <h4>Order Items</h4>
        <span className="badge-soft">{cards.length} lines</span>
      </div>

      <div className="order-detail-list">
        {cards.map((card) => (
          <div key={card.key} className="order-detail-card">
            <div className="order-detail-thumb">
              {card.image ? (
                <img src={getMediaUrl(card.image)} alt={card.name} />
              ) : (
                <div className="product-thumb-fallback">No Image</div>
              )}
            </div>
            <div className="order-detail-info">
              <div className="row">
                <strong>{card.name}</strong>
                <span className="badge-soft">{card.type}</span>
              </div>

              <div className="order-line-meta">
                <div><span className="muted">Unit</span> {formatCurrency(card.unitPrice)}</div>
                <div><span className="muted">Subtotal</span> {formatCurrency(card.subtotal)}</div>
                <div><span className="muted">Qty</span> {card.variants.length ? '-' : card.qty}</div>
              </div>

              {card.variants.length ? (
                <div className="order-variant-list">
                  {card.variants.map((variant) => (
                    <div key={variant.key} className="order-variant-item">
                      <div className="row">
                        <span className="badge-pill">{variant.color}</span>
                        <span className="muted">{variant.packs} pack x {variant.perPack} pcs</span>
                        <span className="muted">Total {variant.pieces} pcs</span>
                      </div>
                      {variant.split ? <div className="muted">Size Split: {variant.split}</div> : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {card.productsDetail.length ? (
                <div className="order-detail-sublist">
                  <div className="muted">Included Products</div>
                  <div className="order-bundle-grid">
                    {card.productsDetail.map((p, pIdx) => (
                      <div key={`pd-${card.key}-${pIdx}`} className="order-bundle-item">
                        <div className="order-bundle-thumb">
                          {p.image || (Array.isArray(p.images) && p.images[0]) ? (
                            <img src={getMediaUrl(p.image || p.images[0])} alt={p.name || p.product_id} />
                          ) : (
                            <span>No Image</span>
                          )}
                        </div>
                        <div>
                          <div className="order-bundle-name">{p.name || p.product_id}</div>
                          <div className="muted">
                            {p.total_quantity || p.quantity_per_combo || 0} pcs
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdersDetailPage;
